import { Routes, Route, Navigate } from 'react-router-dom';
import { PlaygroundApp } from '@/playground/PlaygroundApp';
import { OnboardingWizard } from '@/components/Onboarding/OnboardingWizard';
import { WizardDataProvider } from '@/components/shared/layouts';
import { createDefaultOnboardingDraft } from '@/components/Onboarding/services/onboardingDraftUtils';
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';
import { Brand } from '@enfomentis/shared/config';
import { Dashboard } from '@/components/Dashboard';
import { Settings } from '@/components/Settings';
import { TradingHub } from '@/components/TradingHub';
import { AnalysisHub } from '@/components/AnalysisHub';
import {
  ProtectedRoute,
  PublicRoute,
  LoginForm,
  PasswordResetForm,
  PasswordResetConfirmForm,
  AccountActivation,
  EmailChangeVerification
} from '@/components/Auth';
import { BetaApplicationWizard } from '@/components/BetaApplication';
import { createDefaultBetaApplicationDraft } from '@/components/BetaApplication/types/BetaApplicationDraft';
import type { BetaApplicationDraft } from '@/components/BetaApplication/types/BetaApplicationDraft';
import { AdminAuth, AdminRoute, AdminDashboard, AdminProfileSettings, BetaApplicationList, BetaApplicationDetail, UserList, UserDetail } from '@/components/Admin';
import { OAuthCallback } from '@/pages/OAuthCallback';
import { CheckYourEmailPage, RegistrationVerification, RegisterPage, SubscribePublicPage } from '@/components/Subscription';
import { PaymentPage, PaymentSuccess, PaymentCancel } from '@/components/Payment';

/**
 * App Router - Authentication Integrated
 *
 * Available routes:
 *
 * Authentication Routes (Public):
 * - /login → LoginForm (redirects to dashboard if authenticated)
 * - /beta-application → BetaApplicationForm (redirects to dashboard if authenticated)
 * - /password-reset → PasswordResetForm (redirects to dashboard if authenticated)
 * - /password-reset/confirm → PasswordResetConfirmForm (token-based password reset)
 * - /activate/:token → AccountActivation (user account activation)
 * - /verify-email/:token → EmailChangeVerification (email address change verification)
 * - /check-email → CheckYourEmailPage (email confirmation after registration)
 * - /verify-registration/:token → RegistrationVerification (complete registration)
 * - /register → RegisterPage (registration form for new users)
 *
 * Subscription Routes (Public - handles auth internally):
 * - /subscribe → SubscribePublicPage (plan selection, redirects to register if not logged in)
 *
 * Protected Routes (Require Authentication):
 * - /dashboard → Main Dashboard (Trading Hub, Analysis Hub, Settings)
 * - /trading → Trading Hub (Pre-trading, Live Trading, Post-trading)
 * - /analysis → Analysis Hub (Cross-session analytics, AI insights, reports)
 * - /settings → Settings Dashboard (App Preferences, Trading Workflow, etc.)
 * - /onboarding → OnboardingWizard (new wizard-based onboarding)
 * - /payment → PaymentPage (Stripe checkout for pending_payment users)
 * - /payment/success → PaymentSuccess (post-checkout success page)
 * - /payment/cancel → PaymentCancel (post-checkout cancel page)
 *
 * Development Routes:
 * - /playground → PlaygroundDashboard (main navigation hub)
 * - /playground/components → ComponentsListing (HeroUI components hub)
 * - /playground/components/link → LinkExamples (individual component demos)
 * - /playground/themes → V4ThemePlayground (Tailwind v4 theme system)
 * - /playground/data → AGGridPlayground (data visualization demos)
 *
 * Route Protection:
 * - Unauthenticated users are redirected to /login with return URL preservation
 * - Authenticated users accessing auth routes are redirected to /dashboard
 */

function App() {
  // Check if running in beta-only mode
  const isBetaOnlyMode = import.meta.env['VITE_APP_MODE'] === 'beta-only';

  // Beta-only mode: Only show beta application
  if (isBetaOnlyMode) {
    return (
      <Routes>
        {/* Beta application route */}
        <Route
          path="/beta-application"
          element={
            <WizardDataProvider<BetaApplicationDraft>
              initialData={createDefaultBetaApplicationDraft()}
              storageKey={`${Brand.cachePrefix}-beta-application-draft`}
            >
              <BetaApplicationWizard />
            </WizardDataProvider>
          }
        />

        {/* All other routes redirect to beta application */}
        <Route path="*" element={<Navigate to="/beta-application" replace />} />
      </Routes>
    );
  }

  // Standard mode: Full application with all routes
  return (
    <Routes>
      {/* ===== OAUTH CALLBACK ROUTE (PUBLIC) ===== */}
      <Route path="/oauth/callback" element={<OAuthCallback />} />

      {/* ===== AUTHENTICATION ROUTES (PUBLIC) ===== */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginForm />
          </PublicRoute>
        }
      />
      <Route
        path="/beta-application"
        element={
          <PublicRoute>
            <WizardDataProvider<BetaApplicationDraft>
              initialData={createDefaultBetaApplicationDraft()}
              storageKey={`${Brand.cachePrefix}-beta-application-draft`}
            >
              <BetaApplicationWizard />
            </WizardDataProvider>
          </PublicRoute>
        }
      />
      <Route
        path="/password-reset"
        element={
          <PublicRoute>
            <PasswordResetForm />
          </PublicRoute>
        }
      />
      <Route
        path="/password-reset/confirm"
        element={
          <PublicRoute>
            <PasswordResetConfirmForm />
          </PublicRoute>
        }
      />
      <Route
        path="/activate/:token"
        element={
          <PublicRoute>
            <AccountActivation />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email/:token"
        element={<EmailChangeVerification />}
      />
      <Route
        path="/check-email"
        element={
          <PublicRoute>
            <CheckYourEmailPage />
          </PublicRoute>
        }
      />
      <Route
        path="/verify-registration/:token"
        element={
          <PublicRoute>
            <RegistrationVerification />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* ===== SUBSCRIPTION ROUTES (PUBLIC - Handles auth internally) ===== */}
      <Route
        path="/subscribe"
        element={<SubscribePublicPage />}
      />

      {/* ===== PROTECTED ROUTES (REQUIRE AUTHENTICATION) ===== */}
      {/* Main dashboard route */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Settings dashboard route */}
      <Route
        path="/settings/*"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Trading hub route - requires draftId param for refresh-safe routing */}
      <Route
        path="/trading/:draftId"
        element={
          <ProtectedRoute>
            <TradingHub />
          </ProtectedRoute>
        }
      />

      {/* Analysis hub route */}
      <Route
        path="/analysis"
        element={
          <ProtectedRoute>
            <AnalysisHub />
          </ProtectedRoute>
        }
      />

      {/* Onboarding wizard route */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <WizardDataProvider<OnboardingDraft>
              initialData={createDefaultOnboardingDraft()}
              storageKey={`${Brand.cachePrefix}-onboarding-draft`}
            >
              <OnboardingWizard />
            </WizardDataProvider>
          </ProtectedRoute>
        }
      />

      {/* ===== PAYMENT ROUTES (REQUIRE AUTHENTICATION) ===== */}
      <Route
        path="/payment"
        element={
          <ProtectedRoute>
            <PaymentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        }
      />

      {/* ===== DEVELOPMENT ROUTES ===== */}
      {/* Playground routes with centralized theme management */}
      <Route path="/playground/*" element={<PlaygroundApp />} />

      {/* ===== ADMIN ROUTES ===== */}
      {/* Admin authentication route (public) */}
      <Route path="/admin/login" element={<AdminAuth />} />

      {/* Protected admin routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/beta-applications"
        element={
          <AdminRoute>
            <BetaApplicationList />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/beta-applications/:id"
        element={
          <AdminRoute>
            <BetaApplicationDetail />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <UserList />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <AdminRoute>
            <UserDetail />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <AdminRoute>
            <AdminProfileSettings />
          </AdminRoute>
        }
      />

      {/* ===== DEFAULT AND CATCH-ALL ROUTES ===== */}
      {/* Smart default route - detects admin subdomain and redirects appropriately */}
      <Route path="/" element={
        <Navigate
          to={window.location.hostname.startsWith('admin.') ? "/admin" : "/dashboard"}
          replace
        />
      } />

      {/* Catch all - smart redirect based on subdomain */}
      <Route path="*" element={
        <Navigate
          to={window.location.hostname.startsWith('admin.') ? "/admin" : "/dashboard"}
          replace
        />
      } />
    </Routes>
  );
}

export default App;